import { blinkConstants } from '@thoughtspot/blink-constants';
import * as Logger from '@thoughtspot/logger';
import { getNullValuePlaceHolder, getSpecialFormatData, } from '@thoughtspot/special-data-value-format';
import { assign } from '@thoughtspot/string-util';
import { strings } from '@thoughtspot/translations';
import _ from 'lodash';
import { addToField, bucketizationToDatePreset, dateFormatPresets, DEFAULT_QUARTER_START_MONTH, formatDateTime, getEndOfField, getEpochFromDateString, getNextDateTimeByBucket, getRelativeTime, getStartOfField, initDateFormat, parseDate, setQuarterStartMonth, setTimeZone, subtractFromField, timeBuckets, } from './date-format';
let quarterStartMonth;
const timeBucketCategories = {
    NO_BUCKET: 'ms',
    HOUR: 'h',
    DAY: 'd',
    WEEK: 'w',
    MONTH: 'M',
    QUARTER: 'Q',
    YEAR: 'y',
};
const timeBucketToBucketCategory = {
    ms: timeBucketCategories.NO_BUCKET,
    d: timeBucketCategories.DAY,
    w: timeBucketCategories.WEEK,
    M: timeBucketCategories.MONTH,
    Q: timeBucketCategories.QUARTER,
    y: timeBucketCategories.YEAR,
    h: timeBucketCategories.HOUR,
    hod: timeBucketCategories.HOUR,
    dow: timeBucketCategories.DAY,
    dom: timeBucketCategories.DAY,
    doq: timeBucketCategories.DAY,
    doy: timeBucketCategories.DAY,
    wom: timeBucketCategories.WEEK,
    woq: timeBucketCategories.WEEK,
    woy: timeBucketCategories.WEEK,
    moq: timeBucketCategories.MONTH,
    moy: timeBucketCategories.MONTH,
    qoy: timeBucketCategories.QUARTER,
};
const millisecondsInIntervalConstants = {
    YEAR: 366 * 86400 * 1000,
    QUARTER: 3 * 30 * 86400 * 1000,
    MONTH: 31 * 86400 * 1000,
    WEEK: 7 * 86400 * 1000,
    DAY: 86400 * 1000,
    HOUR: 60 * 60 * 1000,
    NO_BUCKET: 1,
};
const dateNumTypes = {
    DATE_NUM_ABS_DAY: 'DATE_NUM_ABS_DAY',
    DATE_NUM_ABS_MONTH: 'DATE_NUM_ABS_MONTH',
    DATE_NUM_ABS_QUARTER: 'DATE_NUM_ABS_QUARTER',
    DATE_NUM_ABS_YEAR: 'DATE_NUM_ABS_YEAR',
    DATE_NUM_DAY_IN_MONTH: 'DATE_NUM_DAY_IN_MONTH',
    DATE_NUM_DAY_IN_QUARTER: 'DATE_NUM_DAY_IN_QUARTER',
    DATE_NUM_DAY_IN_YEAR: 'DATE_NUM_DAY_IN_YEAR',
    DATE_NUM_DAY_OF_WEEK: 'DATE_NUM_DAY_OF_WEEK',
    DATE_NUM_MONTH_IN_QUARTER: 'DATE_NUM_MONTH_IN_QUARTER',
    DATE_NUM_MONTH_IN_YEAR: 'DATE_NUM_MONTH_IN_YEAR',
    DATE_NUM_QUARTER_IN_YEAR: 'DATE_NUM_QUARTER_IN_YEAR',
    DATE_NUM_WEEK_IN_YEAR: 'DATE_NUM_WEEK_IN_YEAR',
    DATE_NUM_WEEK_IN_QUARTER: 'DATE_NUM_WEEK_IN_QUARTER',
    DATE_NUM_WEEK_IN_MONTH: 'DATE_NUM_WEEK_IN_MONTH',
    DATE_NUM_HOUR_IN_DAY: 'DATE_NUM_HOUR_IN_DAY',
};
const weekdays = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
];
const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
];
const logger = Logger.create('date-util');
const initialize = async (inputQuarterStartMonth, sessionTimeZone, locale) => {
    quarterStartMonth = inputQuarterStartMonth;
    if (sessionTimeZone) {
        setTimeZone(sessionTimeZone);
    }
    setQuarterStartMonth(quarterStartMonth);
    await initDateFormat(locale);
};
function getSupportedTimeBuckets(effectiveDataType) {
    let supportedBuckets = timeBuckets;
    if (effectiveDataType === 'TIME') {
        supportedBuckets = _.pickBy(timeBuckets, function (value) {
            return (value === timeBuckets.NO_BUCKET || value === timeBuckets.HOURLY);
        });
    }
    return supportedBuckets;
}
function sanitizeDate(inputDate, format) {
    const specialValue = getSpecialFormatData(inputDate);
    if (specialValue) {
        return specialValue;
    }
    if (typeof inputDate === 'string') {
        if (!_.isNaN(Number(inputDate))) {
            return parseInt(inputDate, 10);
        }
        return parseDate(inputDate, true, format);
    }
    return inputDate;
}
function getOrdinalSuffixedValue(i) {
    i = parseInt(i.toString());
    const j = i % 10;
    const k = i % 100;
    if (j == 1 && k != 11) {
        return `${i}st`;
    }
    if (j == 2 && k != 12) {
        return `${i}nd`;
    }
    if (j == 3 && k != 13) {
        return `${i}rd`;
    }
    return `${i}th`;
}
function formatDateNum(effectiveDataType, value, formatPattern, options) {
    if (_.isString(value)) {
        value = parseInt(value.toString());
    }
    if (!value && value !== 0) {
        return strings.NULL_VALUE_PLACEHOLDER_LABEL;
    }
    const specialVal = getSpecialFormatData(value);
    if (specialVal) {
        return specialVal;
    }
    switch (effectiveDataType) {
        case dateNumTypes.DATE_NUM_ABS_DAY:
        case dateNumTypes.DATE_NUM_ABS_MONTH:
        case dateNumTypes.DATE_NUM_ABS_QUARTER:
        case dateNumTypes.DATE_NUM_ABS_YEAR:
            return `${value}`;
        case dateNumTypes.DATE_NUM_DAY_IN_MONTH:
            return formatPattern === blinkConstants.DAY_IN_MONTH_FORMAT
                ? `${value}`
                : `${getOrdinalSuffixedValue(value)} day of month`;
        case dateNumTypes.DATE_NUM_DAY_IN_QUARTER:
            return formatPattern === blinkConstants.DAY_IN_QUARTER_FORMAT
                ? `${value}`
                : `${getOrdinalSuffixedValue(value)} day of quarter`;
        case dateNumTypes.DATE_NUM_DAY_IN_YEAR:
            return formatPattern === blinkConstants.DAY_IN_YEAR_FORMAT
                ? `${value}`
                : `${getOrdinalSuffixedValue(value)} day of year`;
        case dateNumTypes.DATE_NUM_DAY_OF_WEEK:
            return formatPattern === blinkConstants.DAY_OF_WEEK_FORMAT
                ?
                    getDayOfWeek(value)
                : `${value}`;
        case dateNumTypes.DATE_NUM_MONTH_IN_QUARTER:
            return formatPattern === blinkConstants.MONTH_IN_QUARTER_FORMAT
                ? `${value}`
                : `${getOrdinalSuffixedValue(value)} month of quarter`;
        case dateNumTypes.DATE_NUM_MONTH_IN_YEAR:
            return formatPattern === blinkConstants.MONTH_IN_YEAR_FORMAT
                ?
                    getMonthOfYear(value, options)
                : `${value}`;
        case dateNumTypes.DATE_NUM_QUARTER_IN_YEAR:
            return assign(strings.QUARTER_OF_YEAR, value);
        case dateNumTypes.DATE_NUM_WEEK_IN_YEAR:
            return formatPattern === blinkConstants.WEEK_IN_YEAR_FORMAT
                ? `${value}`
                : `${getOrdinalSuffixedValue(value)} week of year`;
        case dateNumTypes.DATE_NUM_WEEK_IN_QUARTER:
        case dateNumTypes.DATE_NUM_WEEK_IN_MONTH:
            return `${value}`;
        case dateNumTypes.DATE_NUM_HOUR_IN_DAY:
            return `${value}`;
        default:
            logger.warn('unknown effectiveDataType for date num', effectiveDataType);
            return value;
    }
}
function getDayOfWeek(num) {
    num %= 7;
    return strings.weekOfDay[weekdays[num]];
}
function getMonthOfYear(num, options) {
    const isGregorianCal = Boolean(options?.isGregorianCalendar);
    let monthNum = num +
        (isGregorianCal ? DEFAULT_QUARTER_START_MONTH : quarterStartMonth) -
        1;
    monthNum = monthNum > 12 ? monthNum - 12 : monthNum;
    return strings.monthOfYear[months[monthNum - 1]];
}
function getNextBucketEpoch(currentEpoch, bucketization) {
    switch (bucketization) {
        case timeBuckets.HOURLY:
        case timeBuckets.DAILY:
        case timeBuckets.WEEKLY:
        case timeBuckets.MONTHLY:
        case timeBuckets.QUARTERLY:
        case timeBuckets.YEARLY:
            return addToField(currentEpoch, bucketization, 1);
        default:
            logger.error('Unsupported bucketization');
            return null;
    }
}
function getPrevBucketEpoch(currentEpoch, bucketization) {
    switch (bucketization) {
        case timeBuckets.HOURLY:
        case timeBuckets.DAILY:
        case timeBuckets.WEEKLY:
        case timeBuckets.MONTHLY:
        case timeBuckets.QUARTERLY:
        case timeBuckets.YEARLY:
            return subtractFromField(currentEpoch, bucketization, 1);
        default:
            logger.error('Unsupported bucketization');
            return null;
    }
}
function getFieldBoundaries(epochMillis, fieldName) {
    return [
        getStartOfField(epochMillis, fieldName),
        getEndOfField(epochMillis, fieldName),
    ];
}
function getHourlyBoundaries(epochMillis) {
    return getFieldBoundaries(epochMillis, timeBuckets.HOURLY);
}
function getDailyBoundaries(epochMillis) {
    return getFieldBoundaries(epochMillis, timeBuckets.DAILY);
}
function getWeeklyBoundaries(epochValue) {
    return getFieldBoundaries(epochValue, timeBuckets.WEEKLY);
}
function getMonthlyBoundaries(epochMillis) {
    return getFieldBoundaries(epochMillis, timeBuckets.MONTHLY);
}
function getQuarterlyBoundaries(epochValue) {
    return getFieldBoundaries(epochValue, timeBuckets.QUARTERLY);
}
function getYearlyBoundaries(epochValue) {
    return getFieldBoundaries(epochValue, timeBuckets.YEARLY);
}
function getTimeBucketStart(epochValue, timeBucket) {
    switch (timeBucket) {
        case timeBuckets.HOURLY:
        case timeBuckets.DAILY:
        case timeBuckets.WEEKLY:
        case timeBuckets.MONTHLY:
        case timeBuckets.QUARTERLY:
        case timeBuckets.YEARLY:
            return getStartOfField(epochValue, timeBucket);
        case timeBuckets.NO_BUCKET:
            return epochValue;
        default:
            return epochValue;
    }
}
function epochToTimeAgoString(msSinceEpoch) {
    if (!_.isNumber(msSinceEpoch)) {
        return 'N/A';
    }
    return getRelativeTime(msSinceEpoch);
}
function epochToTimeStringWithoutSuffix(msSinceEpoch) {
    if (!_.isNumber(msSinceEpoch)) {
        return 'N/A';
    }
    const timeText = getRelativeTime(msSinceEpoch, true) || '';
    if (timeText) {
        return assign(strings.metadataListPage.captions.MODIFIED_TEXT, {
            timeText,
        });
    }
    return 'N/A';
}
function formatDate(inputDate, format, useSystemCalendar = false, options) {
    if (inputDate === void 0 || inputDate === null) {
        return getNullValuePlaceHolder();
    }
    if (!format) {
        format = dateFormatPresets.DATE_SHORT;
    }
    if (inputDate === strings.NULL_VALUE_PLACEHOLDER_LABEL ||
        inputDate === strings.EMPTY_VALUE_PLACEHOLDER_LABEL ||
        inputDate === strings.OTHER_VALUE_PLACEHOLDER_LABEL) {
        return inputDate;
    }
    inputDate = sanitizeDate(inputDate, format);
    if (isNaN(inputDate)) {
        return getNullValuePlaceHolder();
    }
    let epochMillis = inputDate;
    if (_.isDate(epochMillis)) {
        epochMillis = inputDate.getTime();
    }
    if (!_.isNumber(epochMillis)) {
        logger.warn('formatDate could not convert input date to a timestamp', inputDate);
        return `${inputDate}`;
    }
    return formatDateTime(epochMillis, format, useSystemCalendar, options);
}
function getIntervalMillisForTimeBucket(timeBucket) {
    switch (timeBucket) {
        case timeBuckets.HOURLY:
            return millisecondsInIntervalConstants.HOUR;
        case timeBuckets.DAILY:
            return millisecondsInIntervalConstants.DAY;
        case timeBuckets.WEEKLY:
            return millisecondsInIntervalConstants.WEEK;
        case timeBuckets.MONTHLY:
            return millisecondsInIntervalConstants.MONTH;
        case timeBuckets.QUARTERLY:
            return millisecondsInIntervalConstants.QUARTER;
        case timeBuckets.YEARLY:
            return millisecondsInIntervalConstants.YEAR;
        case timeBuckets.NO_BUCKET:
            return millisecondsInIntervalConstants.NO_BUCKET;
        default:
            return 0;
    }
}
function getFormatPatternForBucket(bucket) {
    return bucketizationToDatePreset[bucket];
}
function convertToMilliSeconds(epoch) {
    if (_.isNil(epoch)) {
        return null;
    }
    const multiplier = 1000;
    if (typeof epoch === 'string') {
        return parseInt(epoch, 10) * multiplier;
    }
    return epoch * multiplier;
}
function convertToSeconds(ms) {
    if (_.isNil(ms)) {
        return null;
    }
    const factor = 1000;
    let result;
    if (typeof ms === 'string') {
        result = parseInt(ms, 10) / factor;
    }
    else {
        result = ms / factor;
    }
    return Math.floor(result);
}
function getEpochFromDate(date, convertToMilliseconds) {
    if (_.isNil(date)) {
        return null;
    }
    const epochMs = getEpochFromDateString(date.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
    }), 'MM/dd/yyyy');
    if (!epochMs) {
        logger.error('unexpected error please check the value');
    }
    return !convertToMilliseconds ? convertToSeconds(epochMs) : epochMs;
}
function getIntervalMillisForFormatPattern(formatPattern) {
    if (_.indexOf(formatPattern, 's') >= 0) {
        return getIntervalMillisForTimeBucket(timeBuckets.NO_BUCKET);
    }
    if (_.indexOf(formatPattern, 'h') >= 0) {
        return getIntervalMillisForTimeBucket(timeBuckets.HOURLY);
    }
    if (_.indexOf(formatPattern, 'd') >= 0) {
        return getIntervalMillisForTimeBucket(timeBuckets.DAILY);
    }
    if (_.indexOf(formatPattern, 'M') >= 0) {
        return getIntervalMillisForTimeBucket(timeBuckets.MONTHLY);
    }
    if (_.indexOf(formatPattern, 'Q') >= 0) {
        return getIntervalMillisForTimeBucket(timeBuckets.QUARTERLY);
    }
    if (_.indexOf(formatPattern, 'y') >= 0) {
        return getIntervalMillisForTimeBucket(timeBuckets.YEARLY);
    }
    return getIntervalMillisForTimeBucket(timeBuckets.NO_BUCKET);
}
function getNextDateTimeEpochFunction(timeBucket) {
    switch (timeBucket) {
        case timeBuckets.HOURLY:
            return val => getNextDateTimeByBucket(val, 'hours');
        case timeBuckets.WEEKLY:
            return val => getNextDateTimeByBucket(val, 'weeks');
        case timeBuckets.MONTHLY:
            return val => getNextDateTimeByBucket(val, 'months');
        case timeBuckets.QUARTERLY:
            return val => getNextDateTimeByBucket(val, 'quarters', false);
        case timeBuckets.YEARLY:
            return val => getNextDateTimeByBucket(val, 'years', false);
        case timeBuckets.DAILY:
        default:
            return val => getNextDateTimeByBucket(val, 'days');
    }
}
function isAboveHourlyBucket(timeBucketCategory) {
    return ['d', 'w', 'M', 'Q', 'Y'].includes(timeBucketCategory);
}
function isAboveWeeklyBucket(timeBucketCategory) {
    return ['M', 'Q', 'Y'].includes(timeBucketCategory);
}
const testHooks = {
    millisecondsInIntervalConstants,
};
export { dateNumTypes, epochToTimeAgoString, formatDateTime as formatTimeOfDay, formatDate, formatDateNum, getEpochFromDate, getSupportedTimeBuckets, getHourlyBoundaries, getIntervalMillisForTimeBucket, getIntervalMillisForFormatPattern, getDailyBoundaries, getWeeklyBoundaries, getMonthlyBoundaries, getQuarterlyBoundaries, getYearlyBoundaries, getTimeBucketStart, getNextBucketEpoch, getPrevBucketEpoch, getDayOfWeek, getMonthOfYear, getFormatPatternForBucket, initialize, timeBucketCategories, timeBucketToBucketCategory, epochToTimeStringWithoutSuffix, convertToMilliSeconds, convertToSeconds, testHooks, getNextDateTimeEpochFunction, isAboveHourlyBucket, isAboveWeeklyBucket, };
//# sourceMappingURL=date-util.js.map